import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { ExportButton, MarkdownExport } from '../components/ExportButtons.js';
import { GradeRing } from '../components/GradeRing.js';
import { LogoMark } from '../components/Layouts.js';
import { useReportBranding } from '../components/ReportBranding.js';
import { TILE_TONE, useLocale, type TileStatus } from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, SeverityBadge } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime, formatNumber } from '../i18n.js';
import { useMe } from '../session.js';
import { SEVERITIES } from './Overview.js';
import { verdictOf } from './AiSearch.js';
import { pageOf, rate } from './Speed.js';

const URGENCIES = ['now', 'soon', 'when_possible', 'tip'] as const;

/** A section of the document with its heading. */
function Part({ title, children }: { title: string; children: React.ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id}>
      <h2 id={id}>{title}</h2>
      {children}
    </section>
  );
}

/** A measured value, rated as on its own page; the rating read out, not only shown in colour. */
function Rated({ value, status }: { value: string; status: TileStatus }) {
  const { t } = useTranslation();
  return (
    <dd className={status === 'unknown' ? 'mono' : `mono ${status}`}>
      {value}
      {status === 'unknown' ? null : (
        <span className="sr-only"> – {t(`tileStatus.${status}`)}</span>
      )}
    </dd>
  );
}

/**
 * Report of one site (T-43, site-report.html and report-print.html): the exports, and the state,
 * areas, open problems with what to do, speed and AI search on one page that prints (or saves as
 * PDF) on light paper without the app around it. Not on the Free plan (audit decision 2, 12.10).
 */
export function Report() {
  const { siteId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const timeZone = me.data?.account.timezone;
  const allowed = me.data ? me.data.account.planCode !== 'free' : null;
  const lang = i18n.language;

  const branding = useReportBranding();
  const brand = branding.data?.available ? branding.data : null;
  const branded = Boolean(brand?.logo || brand?.name);
  const overview = useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', { params: { path: { siteId }, query: { locale } } }),
      ),
  });
  const problems = useQuery({
    queryKey: ['site', siteId, 'findings', 'grouped', locale],
    enabled: allowed === true,
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/findings', {
          params: { path: { siteId }, query: { grouped: 'true', status: 'open', locale } },
        }),
      ),
  });
  const perf = useQuery({
    queryKey: ['site', siteId, 'performance', 'report'],
    enabled: allowed === true,
    queryFn: () => unwrap(api.GET('/sites/{siteId}/performance', { params: { path: { siteId } } })),
  });
  const ai = useQuery({
    queryKey: ['site', siteId, 'ai-visibility', 'report'],
    enabled: allowed === true,
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/ai-visibility', { params: { path: { siteId } } })),
  });

  // The way back and the exports come first, whatever the state of the report.
  const header = (
    <>
      <div className="area-hd">
        <Link className="crumb" to={`/sites/${siteId}`}>
          <span aria-hidden="true">← </span>
          {t('area.back')}
        </Link>
        <h1>{t('report.open')}</h1>
      </div>
      <div className="acts">
        <ExportButton siteId={siteId} format="csv" size="sm" />
        <MarkdownExport siteId={siteId} size="sm" />
        {allowed ? (
          <Button size="sm" onClick={() => window.print()}>
            {t('report.print')}
          </Button>
        ) : null}
      </div>
    </>
  );
  if (overview.isPending || me.isPending)
    return (
      <>
        {header}
        <Loading />
      </>
    );
  if (overview.isError)
    return (
      <>
        {header}
        <LoadError
          message={errorMessage(overview.error, t)}
          onRetry={() => void overview.refetch()}
        />
      </>
    );
  const o = overview.data;
  const site = o.site;
  // The home page on the phone: the first measurement may be another page of the plan.
  const mobile =
    perf.data?.latest.find((s) => s.strategy === 'mobile' && pageOf(s.url) === '/') ?? null;
  const lcp = mobile ? (mobile.field?.lcpMs ?? mobile.lab.lcpMs) : null;
  const groups = problems.data?.groups ?? [];
  // What fails now – the site down, a certificate run out – comes first, the worst first
  // (SP-A2.73). An incident and the open finding of its rule are one problem: the incident's row
  // stands for both, the finding's places count once.
  const incidents = [...o.openIncidents].sort(
    (a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity),
  );
  const grouped = new Set(groups.map((g) => g.ruleCode));
  const incidentsOf = new Map<string, number>();
  for (const i of incidents) incidentsOf.set(i.ruleCode, (incidentsOf.get(i.ruleCode) ?? 0) + 1);
  const counts: Record<string, number> = { now: 0, soon: 0, when_possible: 0, tip: 0 };
  // A rule counts its places or its incidents, whichever are more: as the rows below show it.
  for (const g of groups) {
    const u = g.explanation?.urgency ?? 'tip';
    counts[u] = (counts[u] ?? 0) + Math.max(g.count, incidentsOf.get(g.ruleCode) ?? 0);
  }
  const placesOf = (ruleCode: string) => groups.find((g) => g.ruleCode === ruleCode)?.count ?? 0;
  for (const incident of incidents) {
    if (grouped.has(incident.ruleCode)) continue;
    const u = incident.explanation?.urgency ?? 'now';
    counts[u] = (counts[u] ?? 0) + 1;
  }
  const covered = new Set(incidents.map((i) => i.ruleCode));
  // The findings only once they loaded: an old answer must not stand beside an error.
  const findingRows = problems.isSuccess ? groups.filter((g) => !covered.has(g.ruleCode)) : [];
  const uptime = o.availability.uptime30d;

  return (
    <>
      {header}

      {allowed === false ? (
        <div className="upsell">
          <span>{t('report.planNeeded')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('report.upgrade')}
          </LinkButton>
        </div>
      ) : null}

      {allowed ? (
        <article className="doc" aria-label={t('report.title')}>
          <div className="dochead">
            {branded ? (
              // The customer's own branding (T-66): their logo and name lead the report.
              <div className="client">
                {brand?.logo ? (
                  <img src={brand.logo} alt={brand.name ?? t('branding.logoAlt')} />
                ) : null}
                {brand?.name ? <b>{brand.name}</b> : null}
              </div>
            ) : (
              <span className="logo">
                <LogoMark />
                {t('app.name')} · {t('report.title')}
              </span>
            )}
            <div className="meta">
              <b>{site.host}</b>
              <span>{site.url}</span>
              <span>
                {t('report.createdAt', {
                  time: formatDateTime(new Date().toISOString(), lang, timeZone),
                })}
              </span>
            </div>
          </div>

          <Part title={t('report.summary')}>
            {o.health ? (
              <div className="ring">
                <GradeRing score={o.health.score} grade={o.health.grade} />
                <div>
                  <div className="num">
                    {o.health.score}
                    <small>/ 100</small>
                  </div>
                  <div className="cap">
                    {t('report.grade', { grade: o.health.grade, label: o.health.label })}
                    {uptime !== null
                      ? ` · ${t('report.uptime', { value: formatNumber(uptime, lang, 2) })}`
                      : null}
                  </div>
                </div>
              </div>
            ) : (
              <p className="bodyM">{t('health.pending')}</p>
            )}
            <div className="cnt4">
              {URGENCIES.map((u) => (
                <div key={u}>
                  <span>{t(`report.urgency.${u}`)}</span>
                  <b className={u === 'now' ? 'bad' : u === 'soon' ? 'warn' : undefined}>
                    {/* No zeros for problems we could not load (SP-A2.72). */}
                    {problems.isSuccess ? counts[u] : '–'}
                  </b>
                </div>
              ))}
            </div>
          </Part>

          <Part title={t('report.areas')}>
            {/* On a phone the status moves under the area's name (site.css); should the table still
                be wider, it scrolls in its own box, focusable as such a region must be (TableBox). */}
            <div className="scroll" tabIndex={0} role="region" aria-label={t('report.areas')}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">{t('report.area')}</th>
                    <th scope="col" className="st">
                      {t('report.status')}
                    </th>
                    <th scope="col" className="num">
                      {t('report.points')}
                    </th>
                    <th scope="col" className="num">
                      {t('report.openCount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {o.tiles.map((tile) => {
                    // The area's score today: the last day of its trend, recomputed with every check –
                    // none while the site cannot be checked (bot protection, password): an old day's
                    // score is no score of today (review #185).
                    const points =
                      o.health && tile.status !== 'setup' ? tile.trend.at(-1) : undefined;
                    return (
                      <tr key={tile.key}>
                        <th scope="row">
                          {t(`site.tiles.${tile.key}`)}
                          {/* Decision 2 of SP-UX10.5: the area's fact in small type. */}
                          <small>{tile.headline}</small>
                          {/* On a phone the status sits here, its column hidden (site.css). */}
                          <span className={`badge ${TILE_TONE[tile.status]} st-row`}>
                            {t(`tileStatus.${tile.status}`)}
                          </span>
                        </th>
                        <td className="st">
                          <span className={`badge ${TILE_TONE[tile.status]}`}>
                            {t(`tileStatus.${tile.status}`)}
                          </span>
                        </td>
                        <td className="num">{points ?? '–'}</td>
                        <td className="num">{o.openFindingsByCategory[tile.key] ?? 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Part>

          <Part title={t('report.problems')}>
            {incidents.length > 0 || findingRows.length > 0 ? (
              <table className="probs">
                <tbody>
                  {incidents.map((incident) => (
                    <tr key={incident.id}>
                      <td>
                        <SeverityBadge severity={incident.severity} />
                      </td>
                      <td>
                        <b>{incident.title}</b>
                        {incident.explanation?.action ? (
                          <span>
                            {incident.explanation.action}
                            {/* One incident standing for a finding of several places. */}
                            {incidentsOf.get(incident.ruleCode) === 1 &&
                            placesOf(incident.ruleCode) > 1
                              ? ` · ${t('findings.count', { count: placesOf(incident.ruleCode) })}`
                              : null}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {findingRows.map((g) => (
                    <tr key={g.ruleCode}>
                      <td>
                        <SeverityBadge severity={g.severity} />
                      </td>
                      <td>
                        <b>{g.title}</b>
                        {/* Decision 2 of SP-UX10.5: what to do and how many places, no long text. */}
                        {g.explanation?.action || g.count > 1 ? (
                          <span>
                            {g.explanation?.action}
                            {g.count > 1
                              ? `${g.explanation?.action ? ' · ' : ''}${t('findings.count', { count: g.count })}`
                              : null}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {/* One state at a time for the findings (review of SP-A2.72); incidents show anyway. */}
            {problems.isPending ? (
              <Loading />
            ) : problems.isError ? (
              <LoadError
                message={errorMessage(problems.error, t)}
                onRetry={() => void problems.refetch()}
              />
            ) : incidents.length === 0 && findingRows.length === 0 ? (
              <p className="bodyM">{t('findings.empty.open')}</p>
            ) : null}
          </Part>

          <Part title={t('report.speed')}>
            {perf.isPending ? (
              <Loading />
            ) : perf.isError ? (
              <LoadError
                message={errorMessage(perf.error, t)}
                onRetry={() => void perf.refetch()}
              />
            ) : mobile ? (
              <dl className="kv">
                <div>
                  <dt>{t('speed.score')}</dt>
                  <Rated
                    value={mobile.score !== null ? `${mobile.score} / 100` : '–'}
                    status={rate('score', mobile.score)}
                  />
                </div>
                <div>
                  <dt>LCP</dt>
                  <Rated
                    // To the hundredth, as on the speed page: 2,54 s is not the good 2,5 s.
                    value={lcp !== null ? `${formatNumber(lcp / 1000, lang, 2)} s` : '–'}
                    status={rate('lcpMs', lcp)}
                  />
                </div>
                <div>
                  <dt>{t('report.measured')}</dt>
                  <dd className="mono">{formatDate(mobile.measuredAt, lang, timeZone)}</dd>
                </div>
              </dl>
            ) : (
              <p className="bodyM">{t('speed.none')}</p>
            )}
          </Part>

          <Part title={t('report.ai')}>
            {ai.isPending ? (
              <Loading />
            ) : ai.isError ? (
              <LoadError message={errorMessage(ai.error, t)} onRetry={() => void ai.refetch()} />
            ) : ai.data.score != null ? (
              <dl className="kv">
                <div>
                  <dt>{t('speed.score')}</dt>
                  <dd className="mono">
                    {ai.data.score} / 100 · {t(`ai.grade.${verdictOf(ai.data.score)}`)}
                  </dd>
                </div>
                {ai.data.blockedSearch.length > 0 ? (
                  <div>
                    <dt>{t('report.blocked')}</dt>
                    <dd className="mono">{ai.data.blockedSearch.join(', ')}</dd>
                  </div>
                ) : null}
              </dl>
            ) : (
              <p className="bodyM">{t('ai.none')}</p>
            )}
          </Part>

          <p className="foot">
            <span>
              {/* T-66: with the customer's branding, SitePrio as the tool behind it. */}
              {t(branded ? 'report.footerBranded' : 'report.footer')}
            </span>
            {site.lastFullScanAt ? (
              <span>
                {t('report.checkedAt', {
                  time: formatDateTime(site.lastFullScanAt, lang, timeZone),
                })}
              </span>
            ) : null}
          </p>
        </article>
      ) : null}
    </>
  );
}
